import { type TimeData } from '../core/TimeData';
import { type BaseCost, type BaseIncome } from './baseTypes';
import {
  registerPnlProcessor,
  type PnlProcessorContext,
} from './context/context';

type ProcessorMetadata = {
  id: string;
  dependencies: string[];
};

export interface PnlProcessorError {
  processor: string;
  error: string;
}

export interface PnlProcessorResult<
  CostType extends BaseCost | undefined = undefined,
  IncomeType extends BaseIncome | undefined = undefined,
> {
  // To avoid default distributive behavior of conditional types using generics,
  // we need to surround each side of the extends keyword with square brackets.
  //
  // https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#distributive-conditional-types
  costs: [CostType] extends [undefined] ? undefined : CostType[];
  errors: PnlProcessorError[];
  income: [IncomeType] extends [undefined] ? undefined : IncomeType[];
}

export abstract class PnlProcessor<
  CostType extends BaseCost | undefined = undefined,
  IncomeType extends BaseIncome | undefined = undefined,
  /** Typescript should usually be able to infer this using CostType */
  ResultType = PnlProcessorResult<CostType | undefined, IncomeType | undefined>,
> {
  /** Unique identifier for the processor */
  protected metadata: ProcessorMetadata;
  /** Array of processor ids that this processor depends on */
  protected readonly context: Readonly<PnlProcessorContext>;

  constructor(
    metadata: ProcessorMetadata,
    context: Readonly<PnlProcessorContext>,
  ) {
    this.metadata = metadata;
    registerPnlProcessor(context, metadata.id, metadata.dependencies);
    this.context = context;
  }

  abstract processEvent(timeData: TimeData): void;
  abstract finalize(): void;
  abstract getResult(): ResultType;
}
